import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Shield, Plus, Edit2, Building } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminCyberScores() {
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScore, setEditingScore] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    overall_score: '750',
    network_security: '750',
    endpoint_security: '750',
    patching_cadence: '750',
    identity_management: '750',
    email_security: '750',
    risk_factors: '',
    strengths: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scoresRes, companiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/cyber-scores`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/companies`, { credentials: 'include' })
      ]);

      if (scoresRes.ok) {
        const scoresData = await scoresRes.json();
        setScores(scoresData);
      }

      if (companiesRes.ok) {
        const companiesData = await companiesRes.json();
        setCompanies(companiesData);
      }
    } catch (error) {
      toast.error('Failed to load cyber scores');
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallScore = () => {
    const scores = [
      parseInt(formData.network_security) || 0,
      parseInt(formData.endpoint_security) || 0,
      parseInt(formData.patching_cadence) || 0,
      parseInt(formData.identity_management) || 0,
      parseInt(formData.email_security) || 0
    ];
    const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    setFormData({ ...formData, overall_score: avg.toString() });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const riskFactors = formData.risk_factors.split('\n').filter(r => r.trim());
    const strengths = formData.strengths.split('\n').filter(s => s.trim());

    const payload = {
      company_id: formData.company_id,
      overall_score: parseInt(formData.overall_score),
      network_security: parseInt(formData.network_security),
      endpoint_security: parseInt(formData.endpoint_security),
      patching_cadence: parseInt(formData.patching_cadence),
      identity_management: parseInt(formData.identity_management),
      email_security: parseInt(formData.email_security),
      risk_factors: riskFactors,
      strengths: strengths
    };

    try {
      const url = editingScore
        ? `${BACKEND_URL}/api/cyber-scores/${editingScore.score_id}`
        : `${BACKEND_URL}/api/cyber-scores`;
      
      const response = await fetch(url, {
        method: editingScore ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingScore ? 'Score updated successfully' : 'Score created successfully');
        setDialogOpen(false);
        setEditingScore(null);
        setFormData({
          company_id: '',
          overall_score: '750',
          network_security: '750',
          endpoint_security: '750',
          patching_cadence: '750',
          identity_management: '750',
          email_security: '750',
          risk_factors: '',
          strengths: ''
        });
        fetchData();
      } else {
        throw new Error('Failed to save score');
      }
    } catch (error) {
      toast.error('Failed to save cyber score');
    }
  };

  const handleEdit = (score) => {
    setEditingScore(score);
    setFormData({
      company_id: score.company_id,
      overall_score: score.overall_score.toString(),
      network_security: score.network_security.toString(),
      endpoint_security: score.endpoint_security.toString(),
      patching_cadence: score.patching_cadence.toString(),
      identity_management: score.identity_management.toString(),
      email_security: score.email_security.toString(),
      risk_factors: score.risk_factors.join('\n'),
      strengths: score.strengths.join('\n')
    });
    setDialogOpen(true);
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'bg-primary/10 text-primary border-primary/20';
    if (grade.startsWith('B')) return 'bg-accent/10 text-accent border-accent/20';
    if (grade.startsWith('C')) return 'bg-secondary/10 text-secondary border-secondary/20';
    return 'bg-destructive/10 text-destructive border-destructive/20';
  };

  if (loading) {
    return (
      <AdminLayout title="Cyber Scores">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Cyber Scores">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-secondary" />
            <h2 className="text-2xl font-mono font-bold">Cyber Score Management</h2>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingScore(null);
              setFormData({
                company_id: '',
                overall_score: '750',
                network_security: '750',
                endpoint_security: '750',
                patching_cadence: '750',
                identity_management: '750',
                email_security: '750',
                risk_factors: '',
                strengths: ''
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-mono">
                <Plus className="h-4 w-4 mr-2" />
                {editingScore ? 'Edit Score' : 'Add Score'}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-mono">{editingScore ? 'Edit' : 'Create'} Cyber Score</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="company" className="font-mono">Company *</Label>
                  <Select 
                    value={formData.company_id} 
                    onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                    disabled={!!editingScore}
                  >
                    <SelectTrigger className="font-mono mt-1">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {companies.map((company) => (
                        <SelectItem key={company.company_id} value={company.company_id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="network_security" className="font-mono">Network Security *</Label>
                    <Input
                      id="network_security"
                      type="number"
                      min="0"
                      max="1000"
                      value={formData.network_security}
                      onChange={(e) => setFormData({ ...formData, network_security: e.target.value })}
                      onBlur={calculateOverallScore}
                      required
                      className="font-mono mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="endpoint_security" className="font-mono">Endpoint Security *</Label>
                    <Input
                      id="endpoint_security"
                      type="number"
                      min="0"
                      max="1000"
                      value={formData.endpoint_security}
                      onChange={(e) => setFormData({ ...formData, endpoint_security: e.target.value })}
                      onBlur={calculateOverallScore}
                      required
                      className="font-mono mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="patching_cadence" className="font-mono">Patching Cadence *</Label>
                    <Input
                      id="patching_cadence"
                      type="number"
                      min="0"
                      max="1000"
                      value={formData.patching_cadence}
                      onChange={(e) => setFormData({ ...formData, patching_cadence: e.target.value })}
                      onBlur={calculateOverallScore}
                      required
                      className="font-mono mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="identity_management" className="font-mono">Identity Mgmt *</Label>
                    <Input
                      id="identity_management"
                      type="number"
                      min="0"
                      max="1000"
                      value={formData.identity_management}
                      onChange={(e) => setFormData({ ...formData, identity_management: e.target.value })}
                      onBlur={calculateOverallScore}
                      required
                      className="font-mono mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email_security" className="font-mono">Email Security *</Label>
                    <Input
                      id="email_security"
                      type="number"
                      min="0"
                      max="1000"
                      value={formData.email_security}
                      onChange={(e) => setFormData({ ...formData, email_security: e.target.value })}
                      onBlur={calculateOverallScore}
                      required
                      className="font-mono mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="overall_score" className="font-mono">Overall Score</Label>
                    <Input
                      id="overall_score"
                      type="number"
                      value={formData.overall_score}
                      readOnly
                      className="font-mono mt-1 bg-muted"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="strengths" className="font-mono">Strengths (one per line)</Label>
                  <Textarea
                    id="strengths"
                    value={formData.strengths}
                    onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                    rows={3}
                    className="font-mono mt-1"
                    placeholder="Strong email filtering&#10;Excellent patch management&#10;Robust access controls"
                  />
                </div>

                <div>
                  <Label htmlFor="risk_factors" className="font-mono">Risk Factors (one per line)</Label>
                  <Textarea
                    id="risk_factors"
                    value={formData.risk_factors}
                    onChange={(e) => setFormData({ ...formData, risk_factors: e.target.value })}
                    rows={3}
                    className="font-mono mt-1"
                    placeholder="Outdated endpoints detected&#10;Missing MFA on admin accounts&#10;Unpatched vulnerabilities"
                  />
                </div>

                <Button type="submit" className="w-full font-mono">
                  {editingScore ? 'Update Score' : 'Create Score'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {scores.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Cyber Scores</h3>
            <p className="text-muted-foreground">Create cyber scores for your customers</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/10 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Grade</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Categories</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Last Updated</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {scores.map((score) => {
                    const company = companies.find(c => c.company_id === score.company_id);
                    return (
                      <tr key={score.score_id} className="hover:bg-muted/5">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono font-bold">{company?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-2xl font-mono font-bold text-secondary">{score.overall_score}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-mono border ${getGradeColor(score.grade)}`}>
                            {score.grade}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs font-mono text-muted-foreground space-y-1">
                            <div>Network: {score.network_security}</div>
                            <div>Endpoint: {score.endpoint_security}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-muted-foreground">
                            {new Date(score.last_updated).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleEdit(score)}
                            className="text-secondary hover:text-secondary/80"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminCyberScores;
